import React, {useContext, useEffect, useLayoutEffect, useState} from 'react';
import {Platform, ScrollView, View} from 'react-native';
import {SafeAreaView, useSafeAreaInsets} from 'react-native-safe-area-context';

import Header from '../../components/elements/Header';
import Text from '../../components/elements/Text';
import CheckoutItemCard from '../../components/modules/CheckoutItemCard';
import Button from '../../components/elements/Button';
import CartPriceOverview from '../../components/modules/CartPriceOverview';
import ProductLimitPopup from '../../components/modules/ProductLimitPopup';
import LogoLoader from '../../components/elements/LogoLoader';
import Popup from '../../components/elements/Popup';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {BUTTON_TYPES} from '../../utils/Constants';
import {shopContext} from '../../contexts/ShopContext';
import {convertDateToTime, DLog} from '../../utils/HelperMethods';
import {fetchApi} from '../../utils/ApiHelper';
import {CREATE_ORDER, HTTP_METHODS} from '../../utils/URLConstants';
import {
  useNavigate,
  useOutletContext,
} from '../../utils/mockedWebLibs/react-router-dom';
import MyAddresses from '../MyAddresses';
import {removeSpaceAndCaptalize, tID} from '../../utils/TestIdHelper';
import {trackEvent} from '../../utils/Analytics';
import {CloseIcon, PurchaseSuccessIcon} from '../../utils/SvgIcons';
import {hapticSuccess, hapticError} from '../../utils/Haptics';

export const ProductLimitModal = ({
  showLimitReachedModal = false,
  limitReachedItems = [],
  setShowLimitReachedModal = () => {},
  setShowCartAdjustModal = () => {},
}) => {
  const styles = useThemedStyles(makeStyles);
  return (
    <ProductLimitPopup
      testID={tID('Checkout-ProductLimitPopup-Modal')}
      visible={showLimitReachedModal}
      title={'Free Product Limit Reached'}
      description={
        'You have reached the limit for complimentary products offered by Diathrive Health.\n\nGood news! You can return to place your order when supplies are available to you.'
      }
      limitReachedItems={limitReachedItems}>
      <Button
        testID={tID('ProductLimitPopup-Button-Close')}
        style={[styles.button(0)]}
        onPress={() => {
          setShowLimitReachedModal(false);
          setTimeout(() => {
            setShowCartAdjustModal(true);
          }, 800);
        }}
        title={'Close'}
      />
    </ProductLimitPopup>
  );
};

export const SuccessModal = ({
  showSuccessModal = false,
  setShowSuccessModal = () => {},
  navigate,
  navigation,
}) => {
  const styles = useThemedStyles(makeStyles);
  return (
    <Popup
      testID={tID('Checkout-Success-Modal')}
      showRipple
      rippleIcon={<PurchaseSuccessIcon />}
      visible={showSuccessModal}
      title={'Purchase Successful!'}
      textAlign="center"
      description={
        'You will receive an order confirmation by email with your receipt and delivery tracking code.'
      }>
      <Button
        testID={tID('SuccessModal-Button-Close')}
        style={[styles.button(0)]}
        onPress={() => {
          setShowSuccessModal(false);
          if (Platform.OS === 'web') {
            navigate('/store');
          } else {
            // 'Shopping' is a tab inside HomeTab, not a route on this stack —
            // navigating to it directly was a silent no-op that stranded the
            // user on Checkout with a dead Place Order button. Go home via the
            // tab host, which also pops the whole shop flow off the stack.
            navigation.navigate('HomeTab', {screen: 'HomePage'});
          }
        }}
        title={'Close'}
      />
    </Popup>
  );
};

export const FailedModal = ({
  showFailedModal = false,
  setShowFailedModal = () => {},
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <Popup
      testID={tID('Checkout-Failed-Modal')}
      showRipple
      rippleIcon={<CloseIcon />}
      rippleColor={colors.danger}
      visible={showFailedModal}
      title={'Purchase Unsuccessful!'}
      textAlign="center"
      description={
        'We have encountered an issue while placing your order. Please try again or contact support for further assistance.'
      }>
      <Button
        testID={tID('FailedModal-Button-Close')}
        style={[styles.button(0)]}
        onPress={() => setShowFailedModal(false)}
        title={'Close'}
      />
    </Popup>
  );
};

export const CartAdjustModal = ({
  showCartAdjustModal = false,
  setShowCartAdjustModal = () => {},
  navigate,
  navigation,
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <Popup
      testID={tID('Checkout-CartAdjust-Modal')}
      visible={showCartAdjustModal}
      title={'Your cart has been adjusted!'}
      textAlign="center"
      description={
        'You’ve exceeded the max amount of free items in your cart.\n\nWe’ve updated the complimentary items in your cart to reflect the amount of items you can order.\n\nYou are free to move forward and checkout when you’re ready!'
      }>
      <Button
        testID={tID('CartAdjustModal-Button-Return')}
        buttonType={BUTTON_TYPES.Filled}
        titleColor={colors.textOnAccent}
        style={[styles.button(0)]}
        onPress={() => {
          if (Platform.OS === 'web') {
            navigate('/store', {state: {openCart: true}});
          } else navigation.navigate('MyCart');
          setShowCartAdjustModal(false);
        }}
        title={'Return to Cart'}
      />
    </Popup>
  );
};

export const HeaderTitle = ({
  title = '',
  buttonTitle = '',
  onPress = () => {},
  showCount = false,
  count = 0,
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <View
      testID={tID('HeaderTitle-View-Main')}
      style={styles.titleContainer}
      key={title}>
      <Text
        testID={tID(`HeaderTitle-Text-${removeSpaceAndCaptalize(title)}`)}
        variation="Subtitle1Bold"
        color={colors.link}>
        {title}{' '}
        {showCount && (
          <Text
            testID={tID('HeaderTitle-Text-Count')}
            variation="Subtitle1Regular"
            color={colors.link}>
            ({count})
          </Text>
        )}
      </Text>
      <View style={styles.underlineText}>
        <Text
          testID={tID(
            `HeaderTitle-Text-${removeSpaceAndCaptalize(buttonTitle)}`,
          )}
          onPress={onPress}
          variation="Text1Bold"
          color={colors.link}
          lineHeight={21}>
          {buttonTitle}
        </Text>
      </View>
    </View>
  );
};

export const Row = ({title = '', value = ''}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <View style={styles.rowContainer}>
      <Text
        testID={tID(`Row-Text-${removeSpaceAndCaptalize(title)}`)}
        variation="Text1Regular"
        color={colors.textMuted}
        maxFontSizeMultiplier={2}
        style={styles.rowLabel}>
        {title}
      </Text>
      <Text
        testID={tID(`Row-Text-${removeSpaceAndCaptalize(value)}`)}
        variation="Text1Regular"
        color={value.length > 0 ? colors.textPrimary : colors.borderMuted}
        maxFontSizeMultiplier={2}
        style={styles.rowValue}>
        {value.length > 0 ? value : '—'}
      </Text>
    </View>
  );
};

export const CartPriceSection = ({
  ShopC,
  insets,
  isButtonDisabled,
  onPressPlaceOrder = () => {},
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <>
      <CartPriceOverview sectionTitle={'Total Price'} />
      {!ShopC.defaultAddress && (
        <Text
          testID={tID('Checkout-Text-AddAddress')}
          variation="Text1Regular"
          color={colors.textMuted}
          textAlign="center">
          *You must add a shipping address to continue.
        </Text>
      )}
      <View style={styles.buttonsContainer(insets?.bottom == 0 ? 24 : 0)}>
        <Button
          testID={tID('Checkout-Button-Order')}
          disabled={isButtonDisabled}
          style={styles.button(0)}
          buttonType={BUTTON_TYPES.Filled}
          titleColor={colors.textOnAccent}
          title={Platform.OS === 'web' ? 'Submit Order' : 'Place Order'}
          onPress={onPressPlaceOrder}
          topMargin={Platform.OS === 'web' ? 24 : 10}
        />
      </View>
    </>
  );
};

export const CartShippingAndPayment = ({
  navigate,
  navigation,
  ShopC,
  setMyAddressesVisibility = () => {},
  myAddressesVisibility,
}) => {
  const styles = useThemedStyles(makeStyles);
  return (
    <>
      <HeaderTitle
        title="Cart"
        buttonTitle="edit"
        onPress={() => {
          Platform.OS === 'web'
            ? navigate('/store', {state: {openCart: true}})
            : navigation.navigate('MyCart');
        }}
        showCount={true}
        count={ShopC?.cartProducts?.length}
      />

      {ShopC?.cartProducts?.map((item, index) => (
        <CheckoutItemCard key={index} item={item} index={index} />
      ))}

      <HeaderTitle
        title="Shipping Address"
        buttonTitle={ShopC?.defaultAddress ? 'Edit' : '+ Add'}
        onPress={() => {
          Platform.OS === 'web'
            ? setMyAddressesVisibility(true)
            : navigation.navigate('MyAddresses', {isFromCheckout: true});
        }}
      />

      <View style={styles.cardView} key={'Address'}>
        {myAddressesVisibility ? (
          <MyAddresses
            navigation={navigate}
            setAddOrEditAddress={ShopC?.setAddOrEditAddress}
            addOrEditAddress={ShopC?.addOrEditAddress}
            route={{params: {isFromCheckout: true}}}
            setMyAddressesVisibility={setMyAddressesVisibility}
          />
        ) : (
          <>
            <Row
              title="Address"
              value={
                ShopC?.defaultAddress
                  ? `${ShopC?.defaultAddress?.address1} ${ShopC?.defaultAddress?.address2}`
                  : ''
              }
            />
            <Row
              title="City"
              value={ShopC?.defaultAddress ? ShopC?.defaultAddress?.city : ''}
            />
            <Row
              title="State"
              value={ShopC?.defaultAddress ? ShopC?.defaultAddress?.state : ''}
            />
            <Row
              title="Zip"
              value={ShopC?.defaultAddress ? ShopC?.defaultAddress?.zip : ''}
            />
          </>
        )}
      </View>
    </>
  );
};

const Checkout = ({navigation}) => {
  const insets = useSafeAreaInsets();
  const styles = useThemedStyles(makeStyles);

  const navigate = useNavigate();
  const {setHeaderName} = useOutletContext();

  const ShopC = useContext(shopContext);

  useLayoutEffect(() => {
    if (Platform.OS === 'web' && setHeaderName) {
      setHeaderName('Review Order');
    }
  }, [setHeaderName]);

  useEffect(() => {
    if (Platform.OS === 'web' && ShopC.cartProducts.length == 0) {
      navigate('/store');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only empty-cart redirect; must not re-fire when cart empties after purchase
  }, []);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCartAdjustModal, setShowCartAdjustModal] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [showLimitReachedModal, setShowLimitReachedModal] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [limitReachedItems, setLimitReachedItems] = useState([]);
  const [myAddressesVisibility, setMyAddressesVisibility] = useState(false);

  const checkoutOrder = () => {
    setShowLoading(true);

    let total = 0;
    let grandTotal = 0;
    let shipping = 0;
    let tax = 0;

    ShopC.cartProducts.forEach(e => {
      total = total + parseInt(e.productQty) * parseFloat(e.price);
    });

    // Checkout is intentionally $0 — patients order FREE supplies and nothing is charged, so
    // `shipping` stays 0. Do NOT reinstate the old "$4 shipping under $25" rule here: it
    // predates the free model and would start charging a free-supplies order.

    if (ShopC.defaultAddress && ShopC.defaultAddress.state == 'UT') {
      tax = total * 0.0725;
      grandTotal = grandTotal + tax;
    }

    let params = {
      total: parseFloat(total),
      tax: parseFloat(tax.toFixed(2)),
      shippingCharges: shipping,
    };

    let products = ShopC.cartProducts.map((item, index) => {
      return {
        id: item.id,
        quantity: item.productQty,
        price: parseFloat(item.price),
      };
    });

    params['products'] = products;

    if (ShopC.defaultAddress) {
      if (
        ShopC.defaultAddress.address1 &&
        ShopC.defaultAddress.address1.length > 0
      ) {
        params['shippingAddress'] = ShopC.defaultAddress.address1;
      }
      if (
        ShopC.defaultAddress.address2 &&
        ShopC.defaultAddress.address2.length > 0
      ) {
        params['aptNumber'] = ShopC.defaultAddress.address2;
      }
      if (ShopC.defaultAddress.city && ShopC.defaultAddress.city.length > 0) {
        params['city'] = ShopC.defaultAddress.city;
      }
      if (ShopC.defaultAddress.state && ShopC.defaultAddress.state.length > 0) {
        params['state'] = ShopC.defaultAddress.state;
      }
      if (ShopC.defaultAddress.zip && ShopC.defaultAddress.zip.length > 0) {
        params['zipCode'] = ShopC.defaultAddress.zip;
      }
      if (ShopC.defaultAddress.note && ShopC.defaultAddress.note.length > 0) {
        params['deliveryNote'] = ShopC.defaultAddress.note;
      }
    }
    // DLog(params);
    fetchApi(HTTP_METHODS.POST, CREATE_ORDER, params)
      .then(res => {
        let checkedOutProductsIds = products.map(p => p.id);
        trackEvent('Complete Checkout', {productid: checkedOutProductsIds});
        // DLog('RES => ', res);
        setShowLoading(false);
        ShopC.setCartProducts([]);
        ShopC.setDefaultAddress(null);
        hapticSuccess();
        setTimeout(() => {
          setShowSuccessModal(true);
        }, 800);
      })
      .catch(err => {
        setShowLoading(false);

        // DLog(err);
        if (typeof err != 'string') {
          const data = (err && err.data && err.data.data) || {};
          if (data.insufficient_quantiy?.length > 0) {
            onAdjustCart(data.insufficient_quantiy, 'insufficient_quantiy');
          } else if (data.limit_exceeded?.length > 0) {
            onAdjustCart(data.limit_exceeded, 'limit_exceeded');
          } else {
            hapticError();
            setTimeout(() => {
              setShowFailedModal(true);
            }, 800);
          }
        } else {
          hapticError();
          setTimeout(() => {
            setShowFailedModal(true);
          }, 800);
        }
        // esc.showError(err);
      });
  };

  const onPressPlaceOrder = () => {
    // setShowSuccessModal(true);
    checkoutOrder();
  };

  const onAdjustCart = (data, tag) => {
    let cartItems = [...ShopC.cartProducts];

    if (tag == 'insufficient_quantiy') {
      setTimeout(() => {
        setShowCartAdjustModal(true);
      }, 800);
      ShopC.cartProducts.forEach((p, idx) => {
        data.forEach(i => {
          if (p.id == i.id) {
            cartItems[idx] = {...p, productQty: i.availableQuantity};
          }
        });
      });
    }
    if (tag == 'limit_exceeded') {
      let productsUnavailable = [];
      ShopC.cartProducts.forEach((p, idx) => {
        data.forEach(i => {
          if (p.id == i.id) {
            productsUnavailable.push({
              title: p.title,
              dateAvailable: convertDateToTime(i.nextResetDate),
            });
            setLimitReachedItems(productsUnavailable);
            setTimeout(() => {
              setShowLimitReachedModal(true);
            }, 800);
            cartItems[idx] = {...p, productQty: i.availableQuantityToOrder};
          }
        });
      });
      cartItems = cartItems.filter(item => item.productQty > 0);
    }
    ShopC.setCartProducts(cartItems);

    // const p = cartItems[index];
    // const product = {...p, productQty: p.productQty + productQty};
    // cartItems[index] = product;
  };

  let total = 0;

  ShopC.cartProducts.forEach(e => {
    total = total + parseInt(e.productQty) * parseFloat(e.price);
  });

  // All supplies are $0 for members; there is no payment surface in the app.
  // A cart with total > 0 is unpurchasable by design and the button stays
  // disabled for it.
  let isButtonDisabled = true;
  if (total == 0 && ShopC.defaultAddress && ShopC.cartProducts.length > 0) {
    isButtonDisabled = false;
  }

  //show different layouts according to environment
  const Layout = () => (
    <View style={styles.flexDirection}>
      <View style={Platform.OS === 'web' && styles.width65}>
        <CartShippingAndPayment
          navigate={navigate}
          navigation={navigation}
          ShopC={ShopC}
          setMyAddressesVisibility={setMyAddressesVisibility}
          myAddressesVisibility={myAddressesVisibility}
        />
      </View>
      <View style={Platform.OS === 'web' && styles.width35}>
        <CartPriceSection
          ShopC={ShopC}
          insets={insets}
          isButtonDisabled={isButtonDisabled}
          onPressPlaceOrder={onPressPlaceOrder}
        />
      </View>
    </View>
  );

  return (
    <SafeAreaView
      testID={tID('Checkout-SafeAreaView-Main')}
      style={styles.container}>
      {Platform.OS !== 'web' && (
        <View style={[styles.marginHorizontal24, styles.marginTop16]}>
          <Header title="Checkout" onPressLeftIcon={navigation.goBack} />
        </View>
      )}
      <ScrollView
        keyboardShouldPersistTaps="handled"
        testID={tID('Checkout-ScrollView-1')}
        contentContainerStyle={styles.scrollContainer}>
        <Layout />
      </ScrollView>

      <SuccessModal
        showSuccessModal={showSuccessModal}
        setShowSuccessModal={setShowSuccessModal}
        navigate={navigate}
        navigation={navigation}
      />
      <FailedModal
        showFailedModal={showFailedModal}
        setShowFailedModal={setShowFailedModal}
      />
      <CartAdjustModal
        showCartAdjustModal={showCartAdjustModal}
        setShowCartAdjustModal={setShowCartAdjustModal}
        navigate={navigate}
        navigation={navigation}
      />
      <ProductLimitModal
        showLimitReachedModal={showLimitReachedModal}
        limitReachedItems={limitReachedItems}
        setShowLimitReachedModal={setShowLimitReachedModal}
        setShowCartAdjustModal={setShowCartAdjustModal}
      />
      {showLoading && (
        <LogoLoader testID={tID('Checkout-LogoLoader-Modal')} visible={true} />
      )}
    </SafeAreaView>
  );
};

export default Checkout;
